import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Copy, ExternalLink, X, Loader2, Calendar, CheckCircle2, Video, Trash2, Presentation, FileText, BarChart3, Mail, Sparkles } from 'lucide-react';
import GradientAvatar from '../components/GradientAvatar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Meetings = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [proposalsById, setProposalsById] = useState({}); // proposal_id → proposal
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ title: '', client_name: '', scheduled_for: '' });
  const [createdMeeting, setCreatedMeeting] = useState(null);
  const [transcriptModal, setTranscriptModal] = useState(null); // {meeting, segments, full_text, loading}
  const [analyticsModal, setAnalyticsModal] = useState(null);   // {meeting, data, loading}

  const openAnalytics = async (m) => {
    setAnalyticsModal({ meeting: m, loading: true, data: null });
    try {
      const res = await fetch(`${BACKEND}/api/meetings/${m.id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load analytics');
      const data = await res.json();
      setAnalyticsModal({ meeting: m, loading: false, data });
    } catch (e) {
      toast.error('Could not load analytics');
      setAnalyticsModal({ meeting: m, loading: false, data: null });
    }
  };

  const openTranscript = async (m) => {
    setTranscriptModal({ meeting: m, loading: true, segments: [], full_text: '' });
    try {
      const res = await fetch(`${BACKEND}/api/meetings/${m.id}/transcript`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load transcript');
      const data = await res.json();
      setTranscriptModal({ meeting: m, loading: false, segments: data.segments || [], full_text: data.full_text || '' });
    } catch (e) {
      toast.error('Could not load transcript');
      setTranscriptModal({ meeting: m, loading: false, segments: [], full_text: '' });
    }
  };

  const copyTranscript = async () => {
    if (!transcriptModal?.full_text) return;
    try {
      await navigator.clipboard.writeText(transcriptModal.full_text);
      toast.success('Transcript copied to clipboard');
    } catch (e) {
      toast.info(transcriptModal.full_text);
    }
  };

  // ─── Smart Follow-up Email ───
  const [followupModal, setFollowupModal] = useState(null); // {meeting, draft, loading, to}
  const draftFollowupEmail = async (m) => {
    setFollowupModal({ meeting: m, loading: true, subject: '', body: '', to: '' });
    try {
      const res = await fetch(`${BACKEND}/api/meetings/${m.id}/transcript/follow-up-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Could not draft email');
      }
      setFollowupModal({
        meeting: m,
        loading: false,
        subject: data.subject || '',
        body: data.body || '',
        to: data.suggested_to_email || '',
        suggested_name: data.suggested_to_name || '',
      });
    } catch (e) {
      toast.error(e.message || 'Could not draft follow-up email');
      setFollowupModal(null);
    }
  };

  const openInEmailClient = () => {
    if (!followupModal) return;
    const to = encodeURIComponent(followupModal.to || '');
    const subject = encodeURIComponent(followupModal.subject || '');
    const body = encodeURIComponent(followupModal.body || '');
    const mailto = `mailto:${to}?subject=${subject}&body=${body}`;
    window.location.href = mailto;
  };

  const copyFollowupEmail = async () => {
    if (!followupModal) return;
    const text = `To: ${followupModal.to || ''}\nSubject: ${followupModal.subject || ''}\n\n${followupModal.body || ''}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Email copied to clipboard');
    } catch (e) {
      toast.info('Could not access clipboard');
    }
  };

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const [mRes, pRes] = await Promise.all([
        fetch(`${BACKEND}/api/meetings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND}/api/proposals`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (mRes.ok) setMeetings(await mRes.json());
      if (pRes.ok) {
        const props = await pRes.json();
        const byId = {};
        props.forEach((p) => { byId[p.id] = p; });
        setProposalsById(byId);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchMeetings(); }, [token]);

  const buildClientUrl = (m) => `${window.location.origin}/join/${m.room_id}?t=${m.guest_token}`;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const body = {
        title: newMeeting.title?.trim() || `Meeting with ${newMeeting.client_name || 'client'}`,
        client_name: newMeeting.client_name?.trim() || null,
        scheduled_for: newMeeting.scheduled_for ? new Date(newMeeting.scheduled_for).toISOString() : null,
      };
      const res = await fetch(`${BACKEND}/api/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to create meeting');
      const created = await res.json();
      setCreatedMeeting(created);
      setShowCreate(false);
      setNewMeeting({ title: '', client_name: '', scheduled_for: '' });
      fetchMeetings();
    } catch (e) {
      toast.error('Could not create meeting');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting permanently?')) return;
    const res = await fetch(`${BACKEND}/api/meetings/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      toast.success('Meeting deleted');
      fetchMeetings();
    } else {
      toast.error('Could not delete');
    }
  };

  const handleEnd = async (id) => {
    const res = await fetch(`${BACKEND}/api/meetings/${id}/end`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { toast.success('Meeting ended'); fetchMeetings(); }
  };

  const attachProposal = async (m) => {
    if (m.proposal_id) {
      toast.info('A consolidation proposal is already attached to this meeting.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND}/api/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'consolidation',
          title: `Consolidation — ${m.client_name || 'client'}`,
          client_name: m.client_name || null,
          meeting_id: m.id,
          payload: {
            // Sensible starter values — broker edits live during the call
            debts: [],
            mortgage: { remainingBalance: 0, interestRate: 6.2, remainingTerm: 25 },
            consolidationRate: 6.2,
          },
        }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Consolidation proposal attached. Open the meeting and click "Present".');
      fetchMeetings();
    } catch (e) {
      toast.error('Could not attach proposal');
    }
  };

  const copyLink = async (m) => {
    const url = buildClientUrl(m);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Client link copied to clipboard');
    } catch (e) {
      toast.info(url);
    }
  };

  const copyRevisitLink = async (m) => {
    const proposal = proposalsById[m.proposal_id];
    if (!proposal?.revisit_token) {
      toast.info('No proposal attached yet — click "Attach Proposal" first.');
      return;
    }
    const url = `${window.location.origin}/p/${proposal.revisit_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Revisit link copied. Share with the client to re-open the proposal anytime.');
    } catch (e) {
      toast.info(url);
    }
  };

  const StatusPill = ({ s }) => {
    const map = {
      scheduled: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      live: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
      ended: 'bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
      expired: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
    };
    return <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${map[s] || map.scheduled}`}>{s}</span>;
  };

  return (
    <div className="min-h-screen bg-page text-text-main pb-24 md:pb-0 p-4 md:p-8" data-testid="meetings-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Video className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
              Live Meetings
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Create a meeting, copy the link, send it to the client. They click and join — no login required.
            </p>
          </div>
          <Button data-testid="create-meeting-btn" onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" /> New Meeting
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
        ) : meetings.length === 0 ? (
          <Card className="dark:bg-slate-900 dark:border-slate-800">
            <CardContent className="py-16 text-center">
              <Video className="h-10 w-10 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-500 dark:text-slate-400">No meetings yet. Click <strong>New Meeting</strong> to create one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="meetings-list">
            {meetings.map((m) => (
              <Card key={m.id} data-testid={`meeting-${m.id}`} className="dark:bg-slate-900 dark:border-slate-800 hover:border-crm-blue/40 transition-colors">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                      {m.client_name && (
                        <GradientAvatar
                          name={m.client_name}
                          role="Client"
                          size="md"
                          subtitle={m.title || 'Meeting'}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-base font-semibold">{m.title || 'Untitled meeting'}</h3>
                          <StatusPill s={m.status} />
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
                          {m.client_name && <span>Client: <strong>{m.client_name}</strong></span>}
                          {m.scheduled_for && (
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(m.scheduled_for).toLocaleString()}
                            </span>
                          )}
                          <span>Created {new Date(m.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => copyLink(m)} data-testid={`copy-${m.id}`} disabled={m.status === 'ended'}>
                        <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Client Link
                      </Button>
                      {!m.proposal_id ? (
                        <Button size="sm" variant="outline" onClick={() => attachProposal(m)} data-testid={`attach-proposal-${m.id}`} disabled={m.status === 'ended'}>
                          <Presentation className="h-3.5 w-3.5 mr-1.5" /> Attach Proposal
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => copyRevisitLink(m)} data-testid={`copy-revisit-${m.id}`}>
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Revisit Link
                        </Button>
                      )}
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/meeting/${m.id}`)} data-testid={`enter-${m.id}`} disabled={m.status === 'ended'}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Enter Room
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openTranscript(m)} data-testid={`transcript-${m.id}`}>
                        <FileText className="h-3.5 w-3.5 mr-1.5" /> Transcript
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openAnalytics(m)} data-testid={`analytics-${m.id}`}>
                        <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Analytics
                      </Button>
                      {m.status === 'live' && (
                        <Button size="sm" variant="outline" onClick={() => handleEnd(m.id)} data-testid={`end-${m.id}`}>
                          End
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(m.id)} data-testid={`delete-${m.id}`} className="text-rose-500 hover:text-rose-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Meeting</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title (optional)</Label>
              <Input value={newMeeting.title} onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })} placeholder="Strategy session — John & Mary" data-testid="new-title" />
            </div>
            <div>
              <Label>Client name (optional)</Label>
              <Input value={newMeeting.client_name} onChange={(e) => setNewMeeting({ ...newMeeting, client_name: e.target.value })} placeholder="John Smith" data-testid="new-client" />
            </div>
            <div>
              <Label>Scheduled time (leave blank = instant)</Label>
              <Input type="datetime-local" value={newMeeting.scheduled_for} onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_for: e.target.value })} data-testid="new-scheduled" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-create">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created confirm modal — show client link */}
      <Dialog open={!!createdMeeting} onOpenChange={(o) => !o && setCreatedMeeting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Meeting created</DialogTitle></DialogHeader>
          {createdMeeting && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">Send the link below to your client. They click and join — no login, no app, no setup.</p>
              <div className="bg-slate-100 dark:bg-slate-800 rounded-md px-3 py-2 text-xs break-all font-mono" data-testid="created-link">
                {buildClientUrl(createdMeeting)}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => copyLink(createdMeeting)} className="bg-emerald-600 hover:bg-emerald-700 flex-1">
                  <Copy className="h-4 w-4 mr-2" /> Copy Link
                </Button>
                <Link to={`/meeting/${createdMeeting.id}`}>
                  <Button variant="outline">
                    <ExternalLink className="h-4 w-4 mr-2" /> Enter Room
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Analytics modal */}
      <Dialog open={!!analyticsModal} onOpenChange={(o) => !o && setAnalyticsModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-emerald-500" />
              Meeting Analytics
              {analyticsModal?.meeting?.client_name && <span className="text-slate-400 text-sm font-normal">— {analyticsModal.meeting.client_name}</span>}
            </DialogTitle>
          </DialogHeader>
          {analyticsModal?.loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : (analyticsModal?.data && analyticsModal.data.slides.length > 0) ? (
            <div className="space-y-4 py-2" data-testid="analytics-body">
              {/* Headline tiles */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Total dwell</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{Math.round(analyticsModal.data.total_dwell_secs / 60)}m {Math.round(analyticsModal.data.total_dwell_secs % 60)}s</p>
                  <p className="text-[10px] text-slate-500">across all slides</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-blue-700">Longest on</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">Slide {(analyticsModal.data.longest_slide_idx ?? 0) + 1}</p>
                  <p className="text-[10px] text-slate-500">{Math.round(analyticsModal.data.longest_slide_secs)}s of attention</p>
                </div>
                <div className={`rounded-lg p-3 border ${analyticsModal.data.accept_recorded ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-[10px] uppercase tracking-wide font-bold ${analyticsModal.data.accept_recorded ? 'text-emerald-700' : 'text-slate-500'}`}>Outcome</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{analyticsModal.data.accept_recorded ? 'Accepted ✅' : 'Pending'}</p>
                  <p className="text-[10px] text-slate-500">proposal status</p>
                </div>
              </div>
              {/* Per-slide bar chart */}
              <div>
                <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-2">Dwell time per slide</p>
                <div className="space-y-1.5">
                  {(() => {
                    const max = Math.max(...analyticsModal.data.slides.map((s) => s.total_secs), 1);
                    return analyticsModal.data.slides.map((s) => {
                      const pct = (s.total_secs / max) * 100;
                      const isLongest = s.slide_idx === analyticsModal.data.longest_slide_idx;
                      return (
                        <div key={s.slide_idx} className="flex items-center gap-2 text-xs" data-testid={`dwell-row-${s.slide_idx}`}>
                          <div className="w-16 text-right text-slate-500 font-medium">Slide {s.slide_idx + 1}</div>
                          <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden relative">
                            <div
                              className={`h-full transition-all ${isLongest ? 'bg-emerald-500' : 'bg-blue-400'}`}
                              style={{ width: `${pct}%` }}
                            />
                            <div className="absolute inset-0 flex items-center px-2">
                              <span className="text-[11px] font-bold text-slate-900">
                                {Math.round(s.total_secs)}s
                                <span className="text-slate-500 font-normal ml-2">· {s.views} view{s.views === 1 ? '' : 's'}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500 text-sm" data-testid="analytics-empty">
              No analytics captured yet. Once you present a proposal in a live meeting, dwell time per slide will appear here.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnalyticsModal(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transcript modal */}
      <Dialog open={!!transcriptModal} onOpenChange={(o) => !o && setTranscriptModal(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-500" />
              Meeting Transcript
              {transcriptModal?.meeting?.client_name && <span className="text-slate-400 text-sm font-normal">— {transcriptModal.meeting.client_name}</span>}
            </DialogTitle>
          </DialogHeader>
          {transcriptModal?.loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : (transcriptModal?.segments && transcriptModal.segments.length > 0) ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto py-2" data-testid="transcript-segments">
              {transcriptModal.segments.map((s) => (
                <div key={s.id} className={`flex gap-3 p-3 rounded-lg ${
                  s.role === 'host' ? 'bg-emerald-50 border border-emerald-100' : 'bg-slate-50 border border-slate-100'
                }`}>
                  <div className="flex-shrink-0 w-20">
                    <p className={`text-[10px] uppercase tracking-wide font-bold ${s.role === 'host' ? 'text-emerald-700' : 'text-slate-700'}`}>{s.role}</p>
                    {s.speaker_name && <p className="text-xs font-medium text-slate-700 truncate">{s.speaker_name}</p>}
                    <p className="text-[10px] text-slate-400">{new Date(s.started_at).toLocaleTimeString()}</p>
                  </div>
                  <div className="flex-1 text-sm text-slate-800 leading-relaxed">{s.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500 text-sm" data-testid="transcript-empty">
              No transcript captured yet. Start a meeting and toggle the record button to capture the conversation.
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setTranscriptModal(null)}>Close</Button>
            {transcriptModal?.full_text && (
              <>
                <Button
                  onClick={copyTranscript}
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="copy-transcript-btn"
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy Transcript
                </Button>
                <Button
                  onClick={() => draftFollowupEmail(transcriptModal.meeting)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="draft-followup-email-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2" /> Draft Follow-up Email
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smart Follow-up Email modal */}
      <Dialog open={!!followupModal} onOpenChange={(o) => !o && setFollowupModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="followup-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-500" />
              Follow-up email
              <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">AI draft</span>
            </DialogTitle>
          </DialogHeader>
          {followupModal?.loading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              <p className="text-sm">Reading the transcript and drafting your email…</p>
            </div>
          ) : followupModal ? (
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="followup-to" className="text-xs uppercase tracking-wide text-slate-500">To</Label>
                <Input
                  id="followup-to"
                  data-testid="followup-to-input"
                  type="email"
                  value={followupModal.to}
                  onChange={(e) => setFollowupModal({ ...followupModal, to: e.target.value })}
                  placeholder={followupModal.suggested_name ? `${followupModal.suggested_name}'s email…` : 'client@example.com'}
                  className="mt-1"
                />
                {!followupModal.to && (
                  <p className="text-[11px] text-amber-600 mt-1">No saved email found for this client — paste one in.</p>
                )}
              </div>
              <div>
                <Label htmlFor="followup-subject" className="text-xs uppercase tracking-wide text-slate-500">Subject</Label>
                <Input
                  id="followup-subject"
                  data-testid="followup-subject-input"
                  value={followupModal.subject}
                  onChange={(e) => setFollowupModal({ ...followupModal, subject: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="followup-body" className="text-xs uppercase tracking-wide text-slate-500">Body</Label>
                <textarea
                  id="followup-body"
                  data-testid="followup-body-input"
                  value={followupModal.body}
                  onChange={(e) => setFollowupModal({ ...followupModal, body: e.target.value })}
                  rows={14}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-800 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">Edit anything before sending. Nothing is sent until you click <span className="font-medium text-slate-600">Open in Email</span>.</p>
              </div>
            </div>
          ) : null}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setFollowupModal(null)} data-testid="followup-close-btn">Close</Button>
            {followupModal && !followupModal.loading && (
              <>
                <Button
                  onClick={copyFollowupEmail}
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="followup-copy-btn"
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy
                </Button>
                <Button
                  onClick={openInEmailClient}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="followup-open-email-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" /> Open in Email
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Meetings;
